import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { parseCalendarDate, type FirePatrolRole } from '@jupi/shared'
import { ApiError, ConfirmDialog, formatDate, useAuth, useSnackbar } from '@jupi/ui'
import {
  useDeleteShift,
  useFirePatrolCrew,
  useSignUp,
  useUpdateShift,
  type FirePatrolShift,
} from './api'

// Sign up for a day, or edit a sign-up. A dispatcher can also sign someone
// else up, change the role and choose (lock) or release the person. A
// person who has been chosen keeps the note but no longer the role.
export default function ShiftDialog({
  date,
  shift,
  onClose,
}: {
  date: string
  shift: FirePatrolShift | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { user, can } = useAuth()
  const { showSnackbar } = useSnackbar()
  const dispatcher = can('flight.firepatrol.dispatch')
  const { data: crew = [] } = useFirePatrolCrew(dispatcher)
  const signUp = useSignUp()
  const update = useUpdateShift()
  const remove = useDeleteShift()

  const [userId, setUserId] = useState(shift?.userId ?? user?.id ?? '')
  const [role, setRole] = useState<FirePatrolRole | ''>(shift?.role ?? '')
  const [note, setNote] = useState(shift?.note ?? '')
  const [confirmRemove, setConfirmRemove] = useState(false)

  const locked = Boolean(shift?.lockedAt)
  const mine = shift?.userId === user?.id
  const roleDisabled = locked && !dispatcher
  const busy = signUp.isPending || update.isPending || remove.isPending

  const fail = (err: unknown) => {
    const code = err instanceof ApiError ? err.violations[0]?.code : undefined
    const known = code && ['no_role', 'not_cleared', 'role_taken'].includes(code)
    showSnackbar(known ? t(`firePatrol.errors.${code}`) : err instanceof ApiError ? err.message : t('common.error'), {
      severity: 'error',
    })
  }

  const save = async () => {
    try {
      if (shift) {
        await update.mutateAsync({ shiftId: shift.shiftId, role: role || null, note: note.trim() || null })
      } else {
        await signUp.mutateAsync({
          shiftDate: date,
          role: role || null,
          note: note.trim() || null,
          ...(dispatcher && userId !== user?.id ? { userId } : {}),
        })
      }
      onClose()
    } catch (err) {
      fail(err)
    }
  }

  const setLock = async (lock: boolean) => {
    if (!shift) return
    try {
      await update.mutateAsync({ shiftId: shift.shiftId, locked: lock })
      onClose()
    } catch (err) {
      fail(err)
    }
  }

  const label = formatDate(parseCalendarDate(date) ?? date)

  return (
    <>
      <Dialog open onClose={onClose} fullWidth maxWidth="xs">
        <DialogTitle>{shift ? `${shift.displayName} · ${label}` : t('firePatrol.signUpFor', { date: label })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {shift && locked ? (
              <Alert severity="success">{mine && !dispatcher ? t('firePatrol.chosenByYou') : t('firePatrol.chosenHint')}</Alert>
            ) : shift ? (
              <Typography variant="body2" color="text.secondary">
                {t('firePatrol.unlockedHint')}
              </Typography>
            ) : null}

            {!shift && dispatcher ? (
              <TextField select label={t('firePatrol.person')} value={userId} onChange={(e) => setUserId(e.target.value)}>
                {user ? <MenuItem value={user.id}>{user.displayName}</MenuItem> : null}
                {crew
                  .filter((c) => c.userId !== user?.id)
                  .map((c) => (
                    <MenuItem key={c.userId} value={c.userId}>
                      {c.displayName}
                    </MenuItem>
                  ))}
              </TextField>
            ) : null}

            <TextField
              select
              label={t('firePatrol.role')}
              value={role}
              onChange={(e) => setRole(e.target.value as FirePatrolRole | '')}
              disabled={roleDisabled}
              helperText={t('firePatrol.roleHint')}
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            >
              <MenuItem value="">{t('firePatrol.roleNone')}</MenuItem>
              <MenuItem value="mc">{t('firePatrol.roleMc')}</MenuItem>
              <MenuItem value="pic">{t('firePatrol.rolePic')}</MenuItem>
            </TextField>

            <TextField
              label={t('firePatrol.note')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              minRows={2}
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {shift && (dispatcher || (mine && !locked)) ? (
            <Button color="error" onClick={() => setConfirmRemove(true)} disabled={busy} sx={{ mr: 'auto' }}>
              {t('firePatrol.remove')}
            </Button>
          ) : null}
          {shift && dispatcher ? (
            <Button onClick={() => setLock(!locked)} disabled={busy} color={locked ? 'inherit' : 'success'}>
              {locked ? t('firePatrol.release') : t('firePatrol.choose')}
            </Button>
          ) : null}
          <Button onClick={onClose}>{t('firePatrol.close')}</Button>
          <Button variant="contained" onClick={save} disabled={busy || !userId}>
            {shift ? t('firePatrol.save') : t('firePatrol.signUp')}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmRemove}
        title={t('firePatrol.remove')}
        message={t('firePatrol.removeConfirm')}
        confirmLabel={t('firePatrol.remove')}
        cancelLabel={t('firePatrol.close')}
        onClose={() => setConfirmRemove(false)}
        onConfirm={async () => {
          setConfirmRemove(false)
          try {
            if (shift) await remove.mutateAsync(shift.shiftId)
            onClose()
          } catch (err) {
            fail(err)
          }
        }}
      />
    </>
  )
}
